import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Plus, Trash2, X, Save, User, GripVertical } from "lucide-react";
import ImageCropAndFocal from "../../components/cms/ImageCropAndFocal";

const GROUPS = [
  { value: "honor", label_es: "Presidencia de Honor", label_fr: "Présidence d'Honneur" },
  { value: "counselors", label_es: "Consejeros Económicos y Comerciales", label_fr: "Conseillers Économiques et Commerciaux" },
  { value: "board", label_es: "Junta Directiva", label_fr: "Conseil d'Administration" },
  { value: "team", label_es: "Equipo Profesional", label_fr: "Équipe Professionnelle" },
];

export default function TeamAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const load = () => api.get("/admin/team").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const blank = {
    name: "", role_es: "", role_fr: "", bio_es: "", bio_fr: "",
    photo_url: "", focal_x: 50, focal_y: 50,
    email: "", linkedin: "", group: "board", order: items.length + 1, active: true,
  };

  const save = async () => {
    setError("");
    try {
      if (editing.id) {
        const { id, ...body } = editing;
        await api.patch(`/admin/team/${id}`, body);
      } else {
        await api.post("/admin/team", editing);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar a esta persona?")) return;
    await api.delete(`/admin/team/${id}`);
    load();
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.group === filter);

  const handleDrop = async (targetId) => {
    const dId = dragId;
    setDragId(null);
    setOverId(null);
    if (!dId || dId === targetId) return;
    const dragItem = items.find((i) => i.id === dId);
    const targetItem = items.find((i) => i.id === targetId);
    if (!dragItem || !targetItem || dragItem.group !== targetItem.group) return;
    const arr = [...items];
    const from = arr.findIndex((i) => i.id === dId);
    const to = arr.findIndex((i) => i.id === targetId);
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setItems(arr);
    try {
      await api.post("/admin/team/reorder", { ids: arr.map((i) => i.id) });
    } catch (e) {
      load();
    }
  };

  return (
    <div data-testid="cms-team">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040]">Junta Directiva y Equipo</h1>
          <p className="text-sm text-gray-600">Personas que aparecen en la página La Cámara, agrupadas por categoría. Arrastre <GripVertical className="w-3.5 h-3.5 inline align-text-bottom text-gray-400" /> para reordenar dentro de cada grupo.</p>
        </div>
        <button onClick={() => setEditing(blank)} data-testid="team-add" className="bg-[#C8102E] text-white px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[#A30D25]">
          <Plus className="w-4 h-4" /> Nueva persona
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="team-filter">
        <button onClick={() => setFilter("all")} className={`px-4 py-2 text-xs font-medium border ${filter === "all" ? "bg-[#0F2040] text-white border-[#0F2040]" : "border-gray-200 text-gray-700"}`}>
          Todas ({items.length})
        </button>
        {GROUPS.map((g) => (
          <button key={g.value} onClick={() => setFilter(g.value)} className={`px-4 py-2 text-xs font-medium border ${filter === g.value ? "bg-[#0F2040] text-white border-[#0F2040]" : "border-gray-200 text-gray-700"}`}>
            {g.label_es} ({items.filter((i) => i.group === g.value).length})
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-left px-3 py-3 w-10"></th>
              <th className="text-left px-5 py-3 w-16">Foto</th>
              <th className="text-left px-5 py-3">Nombre</th>
              <th className="text-left px-5 py-3">Cargo (ES)</th>
              <th className="text-left px-5 py-3">Grupo</th>
              <th className="text-left px-5 py-3">Contacto</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">Sin personas en esta categoría.</td></tr>
            ) : filtered.map((p) => (
              <tr
                key={p.id}
                draggable
                onDragStart={() => setDragId(p.id)}
                onDragEnd={() => { setDragId(null); setOverId(null); }}
                onDragOver={(e) => { e.preventDefault(); if (overId !== p.id) setOverId(p.id); }}
                onDrop={() => handleDrop(p.id)}
                className={`border-t border-gray-100 hover:bg-gray-50 ${dragId === p.id ? "opacity-40" : ""} ${overId === p.id && dragId && dragId !== p.id ? "bg-amber-50 outline outline-2 outline-cecit-gold" : ""}`}
                data-testid={`team-row-${p.id}`}
              >
                <td className="px-3 py-3 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500" data-testid={`team-drag-${p.id}`}>
                  <GripVertical className="w-4 h-4" />
                </td>
                <td className="px-5 py-3">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt="" className="w-10 h-10 object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-[#0F2040] flex items-center justify-center text-cecit-gold"><User className="w-4 h-4" /></div>
                  )}
                </td>
                <td className="px-5 py-3 font-medium text-[#0F2040]">{p.name}</td>
                <td className="px-5 py-3 text-gray-600">{p.role_es}</td>
                <td className="px-5 py-3"><span className="text-xs px-2 py-0.5 bg-gray-100 uppercase tracking-wider">{p.group}</span></td>
                <td className="px-5 py-3 text-xs text-gray-500">
                  {p.email && <span className="block">✉ {p.email}</span>}
                  {p.linkedin && <span className="block text-[#0077B5]">in ✓</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setEditing(p)} className="px-3 py-1.5 text-xs border border-gray-200 hover:border-[#0F2040] mr-2">Editar</button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-gray-400 hover:text-red-600 inline-block align-middle">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="team-editor">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-[#0F2040]">{editing.id ? "Editar persona" : "Nueva persona"}</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-4">
              <Field label="Nombre completo *" value={editing.name} onChange={(v) => setEditing((prev) => ({ ...prev, name: v }))} />
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Grupo *</label>
                <select value={editing.group} onChange={(e) => setEditing((prev) => ({ ...prev, group: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 outline-none text-sm">
                  {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label_es}</option>)}
                </select>
              </div>
              <Field label="Cargo (ES)" value={editing.role_es} onChange={(v) => setEditing((prev) => ({ ...prev, role_es: v }))} />
              <Field label="Cargo (FR)" value={editing.role_fr} onChange={(v) => setEditing((prev) => ({ ...prev, role_fr: v }))} />

              <div className="md:col-span-2">
                <ImageCropAndFocal
                  label="Foto"
                  value={editing.photo_url}
                  onChange={(v) => setEditing((prev) => ({ ...prev, photo_url: v }))}
                  focalX={editing.focal_x ?? 50}
                  focalY={editing.focal_y ?? 50}
                  onFocalChange={({ focal_x, focal_y }) =>
                    setEditing((prev) => ({ ...prev, focal_x, focal_y }))
                  }
                  help="Suba una foto, recorte a cuadrado y haga clic sobre la cara para fijar el punto focal."
                />
              </div>

              <Field label="Email" type="email" value={editing.email} onChange={(v) => setEditing((prev) => ({ ...prev, email: v }))} />
              <Field label="LinkedIn (URL completa)" value={editing.linkedin} onChange={(v) => setEditing((prev) => ({ ...prev, linkedin: v }))} />

              <div className="md:col-span-2">
                <Field label="Bio (ES)" textarea value={editing.bio_es} onChange={(v) => setEditing((prev) => ({ ...prev, bio_es: v }))} />
              </div>
              <div className="md:col-span-2">
                <Field label="Bio (FR)" textarea value={editing.bio_fr} onChange={(v) => setEditing((prev) => ({ ...prev, bio_fr: v }))} />
              </div>

              <Field label="Orden" type="number" value={editing.order} onChange={(v) => setEditing((prev) => ({ ...prev, order: parseInt(v || 0) }))} />
              <label className="flex items-center gap-2 text-sm pt-6">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing((prev) => ({ ...prev, active: e.target.checked }))} className="accent-[#C8102E]" />
                Visible en la web
              </label>

              {error && <p className="text-sm text-red-600 md:col-span-2">{error}</p>}

              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-gray-100 mt-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50">Cancelar</button>
                <button onClick={save} data-testid="team-save" className="bg-[#0F2040] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#1A2F5C]">
                  <Save className="w-4 h-4" /> Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm" />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm" />
      )}
    </div>
  );
}
